import { makeObservable, observable, computed, action } from 'mobx';
import isEmpty from 'lodash/isEmpty';
import FilterState from '@features/categories/mobx/state/FilterState';
import { IFilterOption, TFilterDetail, TFilterSelectOptions } from '@features/categories/typescript';
import { replaceBrowserSearchParams } from '@utils/replaceBrowserSearchParams';

export default class FilterMultiselectState extends FilterState {
  state: Record<string, boolean> = {};

  options: TFilterSelectOptions = {} as TFilterSelectOptions;

  constructor(filter: TFilterDetail) {
    super(filter);
    makeObservable(this, {
      state: observable,
      queryURLString: computed,
      queryNameArray: computed,
      setValue: action,
      resetValue: action,
      value: computed,
      isSet: computed,
    });

    if (filter.options) {
      this.setFilterOptions(filter.options as IFilterOption[]);
    }

    this.state = {};
  }

  get value(): string[] {
    const value: string[] = [];
    Object.keys(this.state).forEach((name) => value.push(name));
    return value;
  }

  get isSet() {
    return !isEmpty(this.state);
  }

  setFilterOptions(options: IFilterOption[]) {
    const newOptions: TFilterSelectOptions = {} as TFilterSelectOptions;

    options.forEach((option) => {
      newOptions[option.value] = {
        name: option.value,
        featureImageUrl: option.image ?? undefined,
        featureValueDescriptive: option.name,
      };
    });

    this.options = newOptions;
  }

  setValue(value: string[], noURLUpdate = false) {
    this.state = {};
    value.forEach((name) => {
      this.state[name] = true;
    });
    if (!noURLUpdate) {
      this.markUserActivity();
      this.setValueToURLString();
    }
  }

  resetValue(noURLUpdate = false) {
    this.setValue([], noURLUpdate);
  }

  setValueFromURLString() {
    if (typeof window === 'undefined') {
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const paramValue = urlParams.get(this.queryNameArray);
    if (paramValue) {
      this.state = {};
      paramValue.split(',').forEach((name) => {
        this.state[name] = true;
      });
    }
  }

  removeValueFromURLString() {
    if (typeof window === 'undefined') {
      return;
    }
    const urlParams = new URLSearchParams(window.location.search);
    urlParams.delete(this.queryNameArray);
    replaceBrowserSearchParams(urlParams);
  }

  setValueToURLString() {
    if (isEmpty(this.value) || typeof window === 'undefined') {
      this.removeValueFromURLString();
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    urlParams.set(this.queryNameArray, this.value.join(','));
    replaceBrowserSearchParams(urlParams);
  }

  get queryNameArray() {
    return `${this.queryName}[]`;
  }

  get queryURLString() {
    const urlParams = new URLSearchParams();
    urlParams.append(this.queryNameArray, this.value.toString());

    return decodeURI(urlParams.toString());
  }
}
